import { FilterableMultiSelect, FormGroup, InlineNotification, Stack, Tag } from '@carbon/react';
import { FieldInput, useReferenceOptions, type FieldDef, type FieldEditorProps } from '@pos/admin-ui';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useMemo } from 'react';

type Settings = Record<string, unknown>;

/** One setting of a trigger or reward, shown as the admin form's own field of that type. */
interface SettingDef {
  key: string;
  type: FieldDef['type'];
  required?: boolean;
  options?: FieldDef['options'];
  ref?: string;
  /** Shown only when another setting has one of these values, e.g. a schedule's day only when monthly. */
  when?: { key: string; values: readonly unknown[] };
}

const opt = (value: string | number, label: string) => ({ value, label: label as I18nKey });

/**
 * The triggers and rewards HO knows (ADR 0079), by the code the server stores, with their settings. A new trigger or reward on
 * the server is a new line here, with its labels in `loyalty.programs`.
 */
const TRIGGERS: Readonly<Record<string, readonly SettingDef[]>> = {
  joined: [{ key: 'includeExisting', type: 'toggle' }],
  birthday: [{ key: 'daysBefore', type: 'number' }],
  tier_reached: [{ key: 'tierIds', type: 'reference', ref: 'membership.member_scheme' }],
  order_milestone: [
    { key: 'orders', type: 'number', required: true },
    { key: 'repeat', type: 'toggle' },
  ],
  schedule: [
    {
      key: 'mode',
      type: 'select',
      required: true,
      options: ['once', 'monthly', 'weekly'].map((m) => opt(m, `loyalty.programs.settings.modes.${m}`)),
    },
    { key: 'onDate', type: 'date', required: true, when: { key: 'mode', values: ['once'] } },
    { key: 'day', type: 'number', required: true, when: { key: 'mode', values: ['monthly'] } },
    {
      key: 'weekday',
      type: 'select',
      required: true,
      options: [1, 2, 3, 4, 5, 6, 7].map((d) => opt(d, `loyalty.programs.settings.weekdays.${d}`)),
      when: { key: 'mode', values: ['weekly'] },
    },
    { key: 'time', type: 'time', required: true },
  ],
  condition: [
    {
      key: 'period',
      type: 'select',
      required: true,
      options: ['once', 'year', 'month', 'week'].map((p) => opt(p, `loyalty.programs.settings.periods.${p}`)),
    },
  ],
  // The segment is the program's own field (FR-CRM-05): no settings here, a note pointing to it.
  segment_joined: [],
};

const REWARDS: Readonly<Record<string, readonly SettingDef[]>> = {
  points: [{ key: 'points', type: 'decimal', required: true }],
  coupon: [
    { key: 'promotionId', type: 'reference', ref: 'promotions.promotion', required: true },
    { key: 'validDays', type: 'number' },
  ],
  gift_voucher: [
    { key: 'voucherBatchId', type: 'reference', ref: 'membership.gift_voucher_batch', required: true },
    { key: 'validDays', type: 'number' },
  ],
};

const parse = (value: unknown): Settings => {
  if (value == null || value === '') return {};
  try {
    const parsed: unknown = typeof value === 'string' ? JSON.parse(value) : value;
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) ? (parsed as Settings) : {};
  } catch {
    return {};
  }
};

/**
 * A schedule's single date and time are kept by the server as one local date and time (`at`, the club's time zone); the form
 * edits them as a date and the schedule's time, like the monthly and weekly ones.
 */
const toForm = (s: Settings): Settings =>
  typeof s.at === 'string' ? { ...s, onDate: s.at.slice(0, 10), time: s.time ?? s.at.slice(11, 16) } : s;

const fromForm = (s: Settings): Settings => {
  const { onDate, ...rest } = s;
  if (rest.mode !== 'once') return rest.mode ? { ...rest, at: undefined } : rest;
  // Until both are given the date is kept as it is (the server then asks for both).
  return typeof onDate === 'string' && typeof rest.time === 'string'
    ? { ...rest, at: `${onDate}T${rest.time}:00` }
    : { ...rest, at: undefined, onDate };
};

/** Keeps a setting's value as the server reads it: times as HH:mm, numbers as numbers, an empty value left out. */
function stored(def: SettingDef, value: unknown): unknown {
  if (value === null || value === '' || value === undefined) return undefined;
  if (def.type === 'time' && typeof value === 'string') return value.slice(0, 5);
  if ((def.type === 'number' || def.type === 'decimal') && typeof value === 'string') return Number(value);
  return value;
}

/** Choosing the trigger or the reward: a list of what HO knows. */
export function PluginChoice({ kind, ...props }: FieldEditorProps & { kind: 'triggers' | 'rewards' }) {
  const codes = Object.keys(kind === 'triggers' ? TRIGGERS : REWARDS);
  const field: FieldDef = {
    ...props.field,
    type: 'select',
    options: codes.map((code) => opt(code, `loyalty.programs.${kind}.${code}`)),
    helper: `loyalty.programs.${kind}.help` as I18nKey,
  };
  return <FieldInput {...props} field={field} />;
}

/**
 * A trigger's or a reward's settings (`trigger_settings`, `reward_settings`, jsonb) as the fields that kind needs, instead of
 * raw JSON; they follow the kind chosen above. Unknown keys are kept as they are.
 */
export function PluginSettings({
  kind,
  field,
  value,
  values,
  invalidText,
  onChange,
}: FieldEditorProps & { kind: 'triggers' | 'rewards' }) {
  const { t } = useTranslation();
  const code = String((kind === 'triggers' ? values.triggerType : values.rewardType) ?? '');
  const defs = (kind === 'triggers' ? TRIGGERS : REWARDS)[code];
  const settings = useMemo(() => toForm(parse(value)), [value]);

  if (!defs) {
    return (
      <InlineNotification
        kind="info"
        lowContrast
        hideCloseButton
        title={t(`loyalty.programs.${kind}.chooseFirst` as I18nKey)}
      />
    );
  }

  if (defs.length === 0) {
    return <InlineNotification kind="info" lowContrast hideCloseButton title={t(`loyalty.programs.${kind}.${code}Help` as I18nKey)} />;
  }

  const set = (def: SettingDef, next: unknown) => {
    const changed: Settings = { ...settings, [def.key]: stored(def, next) };
    // A setting another one hides (a monthly day once the schedule is weekly) is left out.
    const shown = (d: SettingDef) => !d.when || d.when.values.includes(changed[d.when.key]);
    const hidden = new Set(defs.filter((d) => !shown(d)).map((d) => d.key));
    const updated = Object.fromEntries(Object.entries(changed).filter(([key]) => !hidden.has(key)));
    onChange(JSON.stringify(kind === 'triggers' ? fromForm(updated) : updated));
  };

  return (
    <FormGroup legendText={t(field.label)}>
      <Stack gap={5}>
        {defs
          .filter((def) => !def.when || def.when.values.includes(settings[def.when.key]))
          .map((def) =>
            def.type === 'reference' && def.key.endsWith('Ids') ? (
              <ReferencesSetting
                key={def.key}
                def={def}
                kind={kind}
                code={code}
                value={settings[def.key]}
                onChange={(ids) => set(def, ids)}
              />
            ) : (
              <FieldInput
                key={def.key}
                field={{
                  key: `${kind}-${def.key}`,
                  label: `loyalty.programs.settings.${def.key}` as I18nKey,
                  helper: `loyalty.programs.settings.${def.key}Help` as I18nKey,
                  type: def.type,
                  required: def.required,
                  options: def.options,
                  ref: def.ref,
                }}
                value={
                  def.type === 'time' && typeof settings[def.key] === 'string'
                    ? `${String(settings[def.key])}:00`
                    : settings[def.key]
                }
                onChange={(next) => set(def, next)}
              />
            ),
          )}
        {invalidText && <InlineNotification kind="error" lowContrast hideCloseButton title={invalidText} />}
      </Stack>
    </FormGroup>
  );
}

/** Several records, e.g. the tiers a "tier reached" program rewards; none chosen means any. */
function ReferencesSetting({
  def,
  kind,
  code,
  value,
  onChange,
}: {
  def: SettingDef;
  kind: string;
  code: string;
  value: unknown;
  onChange(ids: string[] | undefined): void;
}) {
  const { t } = useTranslation();
  const options = useReferenceOptions(def.ref);
  const items = options.data ?? [];
  const chosen = Array.isArray(value) ? (value as string[]) : [];
  const selected = items.filter((o) => chosen.includes(o.value));
  return (
    <div>
      <FilterableMultiSelect<{ value: string; label: string }>
        id={`${kind}-${code}-${def.key}`}
        titleText={t(`loyalty.programs.settings.${def.key}` as I18nKey)}
        helperText={t(`loyalty.programs.settings.${def.key}Help` as I18nKey)}
        placeholder={t('rules.builder.choose')}
        items={items}
        itemToString={(o) => o?.label ?? ''}
        selectedItems={selected}
        onChange={({ selectedItems }) => {
          const ids = (selectedItems ?? []).map((o) => o.value);
          onChange(ids.length > 0 ? ids : undefined);
        }}
        selectionFeedback="fixed"
      />
      {selected.length > 0 && (
        <Stack orientation="horizontal" gap={2}>
          {selected.map((o) => (
            <Tag key={o.value} type="gray">
              {o.label}
            </Tag>
          ))}
        </Stack>
      )}
    </div>
  );
}
